import React, { useEffect, useRef, useState } from "react";
import { Bookmark, BookmarkPlus, ChevronDown, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const iso = (d) => d.toISOString().slice(0, 10);
// Periodo relativo ("ultimi N giorni") se il range termina oggi, altrimenti null (non salvato)
export const periodDaysOf = (range) => {
  if (!range?.date_from || !range?.date_to || range.date_to !== iso(new Date())) return null;
  const n = Math.round((new Date(range.date_to) - new Date(range.date_from)) / 86400000);
  return n > 0 ? n : null;
};
export const rangeFromDays = (n) => { const to = new Date(); const from = new Date(); from.setDate(to.getDate() - n); return { date_from: iso(from), date_to: iso(to) }; };
export const describeFilter = (f) => [
  f.period_days ? `ultimi ${f.period_days} gg` : null,
  f.stores?.length ? `${f.stores.length} sedi` : "tutte le sedi",
  f.weekdays?.length && f.weekdays.length < 7 ? f.weekdays.map((d) => DAYS[d]).join(" ") : null,
  f.hours ? `${f.hours.replace("-", ":00–")}:00` : null,
  f.fidelity_only ? "solo fedeltà" : null,
].filter(Boolean).join(" · ");
export const normalizeFilter = (f) => ({ stores: f.stores || [], weekdays: f.weekdays || [], hours: f.hours || "", fidelity_only: !!f.fidelity_only, period_days: f.period_days || null });

// value = { stores, weekdays, hours, fidelity_only, period_days }; onApply(filter) applica la combinazione salvata
export function SavedFilters({ value, onApply, testid = "crm-saved-filters", withDefault = false }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const load = () => api.get("/elastic/saved-filters").then((r) => setList(r.data || [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const save = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return;
    setBusy(true);
    try { await api.post("/elastic/saved-filters", { name: name.trim(), ...value, period_days: value.period_days || null }); toast.success(`Filtro "${name.trim()}" salvato`); setName(""); load(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const remove = async (f) => {
    try { await api.delete(`/elastic/saved-filters/${f.id}`); toast.success("Filtro eliminato"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const setDefault = async (f) => {
    try { const { data } = await api.post(`/elastic/saved-filters/${f.id}/default`); toast.success(data.is_default ? `"${f.name}" è il filtro predefinito della Dashboard CRM` : "Filtro predefinito rimosso"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const apply = (f) => { onApply(normalizeFilter(f)); setOpen(false); toast.info(`Filtro "${f.name}" applicato`); };
  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`${testid}-toggle`} className="h-10 px-3 rounded-md bg-zinc-950 border border-zinc-800 text-xs flex items-center gap-2 hover:border-zinc-600">
        <Bookmark className="h-3.5 w-3.5 text-[color:var(--paprika)]" /> Filtri salvati{list.length > 0 && <span className="text-zinc-500">({list.length})</span>}
        <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 right-0 w-[380px] max-w-[90vw] rounded-xl border border-zinc-800 bg-[color:var(--bg-surface)] shadow-2xl p-2 space-y-1" data-testid={`${testid}-menu`}>
          {list.length === 0 && <p className="text-[11px] text-zinc-500 px-2 py-1">Nessun filtro salvato: imposta periodo, sedi, giorni e orari, poi salva la combinazione qui sotto.</p>}
          <div className="max-h-64 overflow-auto space-y-0.5">
            {list.map((f) => (
              <div key={f.id} className="flex items-center gap-1 rounded-md hover:bg-zinc-800/60 px-1" data-testid={`${testid}-item-${f.id}`}>
                <button type="button" onClick={() => apply(f)} className="flex-1 text-left py-1.5 px-1 min-w-0" data-testid={`${testid}-apply-${f.id}`}>
                  <div className="text-xs font-medium flex items-center gap-1">{f.name}{f.is_default && <span className="text-[9px] uppercase tracking-wider text-amber-300 border border-amber-500/40 rounded px-1" data-testid={`${testid}-default-badge-${f.id}`}>predefinito</span>}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{describeFilter(f)}</div>
                </button>
                {withDefault && <button type="button" onClick={() => setDefault(f)} className={`p-1.5 ${f.is_default ? "text-amber-300" : "text-zinc-500 hover:text-amber-300"}`} title={f.is_default ? "Togli predefinito" : "Usa come predefinito all'apertura della Dashboard CRM"} data-testid={`${testid}-default-${f.id}`}><Star className="h-3.5 w-3.5" fill={f.is_default ? "currentColor" : "none"} /></button>}
                <button type="button" onClick={() => remove(f)} className="p-1.5 text-zinc-500 hover:text-red-400" title="Elimina" data-testid={`${testid}-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-1 pt-1 border-t border-zinc-800">
            <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); save(e); } }} placeholder="Nome del filtro corrente…" maxLength={60} className="flex-1 h-8 px-2 rounded-md bg-zinc-950 border border-zinc-800 text-xs" data-testid={`${testid}-name`} />
            <button type="button" onClick={save} disabled={busy || !name.trim()} className="h-8 px-2 rounded-md bg-[color:var(--paprika)] text-white text-xs flex items-center gap-1 disabled:opacity-50" data-testid={`${testid}-save`}><BookmarkPlus className="h-3.5 w-3.5" /> Salva</button>
          </div>
          <p className="text-[10px] text-zinc-500 px-1">Attuale: {describeFilter(value)}{value.period_days === null && value.date_range_custom ? " · periodo personalizzato (non salvato)" : ""}</p>
        </div>
      )}
    </div>
  );
}
